import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as FlexLayout from 'flexlayout-react';
import 'flexlayout-react/style/dark.css';

// Components
import { FinxAiFooter } from '../components/finxai-footer';
import Orders from '../components/orders';
import MktDataGrid from '../components/mktdata';
import TopHeader from '../components/top-header';

// Redux
import { AppDispatch, RootState } from '../store/store';
import {
  addOrUpdateMarketData,
  addOrUpdateStrategy,
  addOrUpdateTradeOrder,
  incrementTradeMessageCount,
  incrementMarketDataMessageCount,
  incrementStrategyMessageCount,
} from '../store/slices/dataSlice';
import { setDataFromWsLoading } from '../store/slices/uiSlice';
import { getWebSocketInstance, setLoginSuccess } from '../store/slices/userSlice';
import { addNewDataId, clearNewDataIds } from '../store/slices/dataTableSlice';

// Services & Utils
import DataGridWebSocketService from '../api/services/data-grid-websocket-service';
import { getCache } from '../utils/cache';
import logger from '../utils/logger';

// Models
import { FinxAiUser } from '../models/finxaiUser';
import { FinxaiTrade } from '../models/finxaiTrade';
import { FinxaiMktData } from '../models/finxaiMktData';
import { FinxaiStrategy } from '../models/finxaiStrategy';
import {
  finxai_mkt_data,
  finxai_strategy,
  finxai_trade,
} from '../models/protobuff-objects/finxai_web_pb';

const Terminal: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  const { tradeOrders, marketData, strategies } = useSelector((state: RootState) => state.data);
  const { loggedInUser } = useSelector((state: RootState) => state.user);

  // Local state
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [tabConfig, setTabConfig] = useState<Array<{ TabName: string; InternalTicker: string; DisplayName: string; OrderBy: number }>>([]);

  // WebSocket instance
  const ws = getWebSocketInstance();

  // ===== Fetch Tab Configuration =====
  useEffect(() => {
    const fetchTabConfig = async () => {
      try {
        const response = await fetch('https://script.google.com/macros/s/AKfycbx1QvWNMjfgbA4klXMn24oGJtC7iKAy3EZd3e0zMU5TCiywGg3ZWGDjZhubeps46g/exec?headers=1&sheet=Quotes',
          {
            method: 'GET',
            redirect: 'follow',
            cache: 'no-cache',
          }
        );
        const data = await response.json();

        setTabConfig(currentConfig => {
          // Deep compare to avoid unnecessary re-renders
          if (JSON.stringify(currentConfig) !== JSON.stringify(data)) {
            logger.debug('Tab configuration updated:', data);
            return data;
          }
          return currentConfig;
        });
      } catch (error) {
        logger.error('Failed to fetch tab configuration:', error);
      }
    };

    fetchTabConfig(); // Initial fetch
    const intervalId = setInterval(fetchTabConfig, 30000); // Poll every 30 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, []);

  // ===== User Authentication =====
  useEffect(() => {
    const savedUser = getCache('loggedInUser');

    if (savedUser) {
      logger.debug('User loaded from cache');
      dispatch(setLoginSuccess(savedUser as FinxAiUser));
      setIsUserLoaded(true);
    } else {
      logger.debug('No user found, redirecting to login');
      navigate('/');
    }
  }, [dispatch, navigate]);

  // ===== WebSocket Subscription =====
  useEffect(() => {
    if (!isUserLoaded || !loggedInUser) return;

    const subscribeToData = () => {
      const subReq = ws.buildSubscriptionRequest(loggedInUser);
      DataGridWebSocketService.sendMessage(subReq.serializeBinary());
      logger.debug('Subscription request sent');
    };

    subscribeToData();

    // Initialize data loading
    setTimeout(() => {
      dispatch(setDataFromWsLoading(false));
      logger.debug('Data initialization complete');
    }, 150);
  }, [isUserLoaded, loggedInUser, dispatch, ws]);

  // ===== WebSocket Message Handlers =====
  const handleTradeMessage = useCallback((order: FinxaiTrade) => {
    const isExisting = tradeOrders.some(o => o.order_id === order.order_id);

    dispatch(addOrUpdateTradeOrder(order));
    dispatch(incrementTradeMessageCount());

    if (!isExisting) {
      dispatch(addNewDataId(order.order_id));
      setTimeout(() => dispatch(clearNewDataIds()), 3000);
    }

    //logger.debug('Trade order processed:', order.order_id);
  }, [tradeOrders, dispatch]);

  const handleMarketDataMessage = useCallback((data: FinxaiMktData) => {
    dispatch(addOrUpdateMarketData(data));
    dispatch(incrementMarketDataMessageCount());
  }, [dispatch]);

  const handleStrategyMessage = useCallback((strategy: FinxaiStrategy) => {
    dispatch(addOrUpdateStrategy(strategy));
    dispatch(incrementStrategyMessageCount());
    //logger.debug('Strategy processed:', strategy);
  }, [dispatch]);

  // ===== WebSocket Message Listener =====
  useEffect(() => {
    const messageHandler = async (message: MessageEvent) => {
      const payload = await ws.handleBinaryMessage(message.data);

      if (!payload) return;


      if (payload instanceof finxai_trade && payload.getTickerSymbol()?.trim()) {
        const order = ws.buildObject(payload, marketData, strategies) as FinxaiTrade;
        handleTradeMessage(order);
      }
      else if (payload instanceof finxai_mkt_data) {
        const mktData = ws.buildObject(payload, marketData, strategies) as FinxaiMktData;
        handleMarketDataMessage(mktData);
      }
      else if (payload instanceof finxai_strategy) {
        const strategy = ws.buildObject(payload, marketData, strategies) as FinxaiStrategy;
        handleStrategyMessage(strategy);
      }
    };

    DataGridWebSocketService.sendOnMessage(messageHandler);
  }, [ws, marketData, strategies, handleTradeMessage, handleMarketDataMessage, handleStrategyMessage]);

  // ===== FlexLayout Configuration =====
  const model = useMemo(() => {
    // Group tab config by TabName
    const groupedTabs = tabConfig.reduce((acc, item) => {
      if (!acc[item.TabName]) {
        acc[item.TabName] = [];
      }
      acc[item.TabName].push(item);
      return acc;
    }, {} as Record<string, typeof tabConfig>);

    // Sort the tabs by the OrderBy property
    for (const tabName in groupedTabs) {
      groupedTabs[tabName].sort((a, b) => (a.OrderBy || 0) - (b.OrderBy || 0));
    }

    // Create dynamic tabs for the right border
    const dynamicTabs = [
      {
        type: 'tab',
        name: 'All Market Data',
        component: 'all-mktdata',
        enableClose: false,
      },
      
      ...Object.keys(groupedTabs).map(tabName => ({
        type: 'tab' as const,
        name: tabName,
        component: `dynamic-${tabName.toLowerCase().replace(/\s+/g, '-')}`,
        enableClose: false,
        config: {
          tickers: groupedTabs[tabName].map(item => item.InternalTicker),
          displayNames: groupedTabs[tabName].reduce((acc, item) => {
            acc[item.InternalTicker] = item.DisplayName;
            return acc;
          }, {} as Record<string, string>)
        }
      }))
    ];

    const json: FlexLayout.IJsonModel = {
      global: {
        tabEnableClose: false,
        tabClassName: "text-xs"
      },
      borders: [
        {
          type: 'border',
          location: 'bottom',
          size: 60,
          selected: 0,
          children: [
            {
              type: 'tab',
              id: '4',
              name: 'Footer',
              component: 'footer',
              enableClose: false,
            },
          ],
        },
        {
          type: 'border',
          location: 'right',
          size: 450,
          selected: 0,
          children: dynamicTabs,
        },
      ],
      layout: {
        type: 'row',
        children: [
          {
            type: 'tabset',
            children: [
              { type: 'tab', name: 'All Orders', component: 'all-orders' },
              { type: 'tab', name: 'Working', component: 'working-orders' },
              { type: 'tab', name: 'Filled', component: 'filled-orders' },
            ],
          },
        ],
      },
    };
    return FlexLayout.Model.fromJson(json);
  }, [tabConfig]);

  const factory = useCallback((node: FlexLayout.TabNode) => {
    const component = node.getComponent();
    if (component === 'all-orders') return <Orders />;
    if (component === 'working-orders') return <Orders orderType="Working" />;
    if (component === 'filled-orders') return <Orders orderType="Filled" />;
    if (component === 'all-mktdata') return <MktDataGrid />;
    if (component === 'footer') return <FinxAiFooter />;
    
    // Handle dynamic tabs
    if (component?.startsWith('dynamic-')) {
      const config = node.getConfig();
      if (config && config.tickers) {
        return <MktDataGrid 
          filterType="Custom" 
          customTickers={config.tickers}
          customDisplayNames={config.displayNames}
        />;
      }
    }
    
    return <div />;
  }, []);

  // ===== Render =====
  if (!isUserLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'white',
        fontSize: '24px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden'
    }}>
      <TopHeader />
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <FlexLayout.Layout model={model} factory={factory} />
      </div>
    </div>
  );
};

export default Terminal;
